import {
  ArrowLeft,
  Check,
  CheckCircle2,
  Cloud,
  CloudOff,
  FilePenLine,
  LoaderCircle,
  LockKeyhole,
  PawPrint,
  RefreshCw,
  Search,
  ShieldCheck,
  Stethoscope,
  Trash2,
  UsersRound,
  WifiOff,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  api,
  ApiError,
  type ClinicalDraftFields,
  type ClinicalDraftSigned,
  type CrmClinicalDraft,
  type CrmPatient,
  type CrmPatientDetail,
} from "./api";
import { useAuth } from "./auth-context";
import {
  clearLocalClinicalDraft,
  loadLocalClinicalDraft,
  saveLocalClinicalDraft,
} from "./clinical-draft-vault";
import { useCrm } from "./crm-context";

type SyncState = "idle" | "local" | "pending" | "synced" | "offline" | "conflict";

interface EditorState {
  patient: CrmPatient;
  draft: CrmClinicalDraft | null;
  fields: ClinicalDraftFields;
}

const EMPTY_FIELDS: ClinicalDraftFields = {
  reason: "",
  findings: "",
  diagnosis: "",
  treatment: "",
  alertsReviewed: false,
};

function draftSnapshot(fields: ClinicalDraftFields): string {
  return JSON.stringify({
    reason: fields.reason.trim(),
    findings: fields.findings.trim(),
    diagnosis: fields.diagnosis.trim(),
    treatment: fields.treatment.trim(),
    alertsReviewed: fields.alertsReviewed,
  });
}

function hasContent(fields: ClinicalDraftFields): boolean {
  return Boolean(
    fields.reason.trim() ||
    fields.findings.trim() ||
    fields.diagnosis.trim() ||
    fields.treatment.trim(),
  );
}

function formatDate(value: string, includeTime = false): string {
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    ...(includeTime ? { timeStyle: "short" as const } : {}),
  }).format(new Date(value));
}

function patientKind(species: "DOG" | "CAT"): string {
  return species === "DOG" ? "Perro" : "Gato";
}

function messageFor(reason: unknown, fallback: string): string {
  return reason instanceof Error ? reason.message : fallback;
}

export function ClinicalDraftsScreen(): ReactNode {
  const { activeOrganization } = useCrm();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPatientId = searchParams.get("patient");
  const [drafts, setDrafts] = useState<CrmClinicalDraft[]>([]);
  const [patients, setPatients] = useState<CrmPatient[]>([]);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [patientDetail, setPatientDetail] = useState<CrmPatientDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [starting, setStarting] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [syncState, setSyncState] = useState<SyncState>("idle");
  const [reviewing, setReviewing] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [discardBusy, setDiscardBusy] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState<ClinicalDraftSigned | null>(null);
  const [onlineRevision, setOnlineRevision] = useState(0);
  const lastSavedRef = useRef("");
  const persistingRef = useRef(false);
  const openedRequestedPatientRef = useRef<string | null>(null);
  const signIdempotencyRef = useRef(crypto.randomUUID());
  const vaultScope = activeOrganization && user ? `${activeOrganization.id}:${user.id}` : null;

  const loadPatientDetail = useCallback(
    async (petId: string) => {
      if (!activeOrganization) return;
      setLoadingDetail(true);
      try {
        setPatientDetail(await api.patient(activeOrganization.id, petId));
      } catch (reason) {
        setNotice(messageFor(reason, "No pudimos cargar las alertas del paciente."));
        setPatientDetail(null);
      } finally {
        setLoadingDetail(false);
      }
    },
    [activeOrganization?.id],
  );

  const openDraft = useCallback(
    (draft: CrmClinicalDraft, patientPool: CrmPatient[]) => {
      const patient = patientPool.find((item) => item.id === draft.petId);
      if (!patient) return;
      const fields: ClinicalDraftFields = {
        reason: draft.reason,
        findings: draft.findings,
        diagnosis: draft.diagnosis,
        treatment: draft.treatment,
        alertsReviewed: draft.alertsReviewed,
      };
      lastSavedRef.current = draftSnapshot(fields);
      setEditor({ patient, draft, fields });
      setStarting(false);
      setReviewing(false);
      setConfirmDiscard(false);
      setSigned(null);
      setSyncState("synced");
      signIdempotencyRef.current = crypto.randomUUID();
      void loadPatientDetail(patient.id);
    },
    [loadPatientDetail],
  );

  const beginPatient = useCallback(
    (patient: CrmPatient): void => {
      lastSavedRef.current = "";
      setEditor({ patient, draft: null, fields: { ...EMPTY_FIELDS } });
      setStarting(false);
      setReviewing(false);
      setConfirmDiscard(false);
      setNotice(null);
      setSyncState("idle");
      signIdempotencyRef.current = crypto.randomUUID();
      void loadPatientDetail(patient.id);
    },
    [loadPatientDetail],
  );

  const load = useCallback(async () => {
    if (!activeOrganization || !user) return;
    setLoading(true);
    setError(null);
    try {
      const [nextDrafts, nextPatients] = await Promise.all([
        api.clinicalDrafts(activeOrganization.id),
        api.patients(activeOrganization.id),
      ]);
      setDrafts(nextDrafts);
      setPatients(nextPatients);
      if (vaultScope) {
        const local = await loadLocalClinicalDraft(vaultScope);
        if (local?.organizationId === activeOrganization.id) {
          const patient = nextPatients.find((item) => item.id === local.petId);
          if (patient) {
            const cloud = local.draftId
              ? (nextDrafts.find((item) => item.id === local.draftId) ?? null)
              : null;
            const cloudFields = cloud
              ? {
                  reason: cloud.reason,
                  findings: cloud.findings,
                  diagnosis: cloud.diagnosis,
                  treatment: cloud.treatment,
                  alertsReviewed: cloud.alertsReviewed,
                }
              : null;
            if (
              cloud &&
              local.revision === cloud.revision &&
              cloudFields &&
              draftSnapshot(local.fields) === draftSnapshot(cloudFields)
            ) {
              clearLocalClinicalDraft(vaultScope);
              return;
            }
            setEditor({ patient, draft: cloud, fields: local.fields });
            lastSavedRef.current = cloudFields ? draftSnapshot(cloudFields) : "";
            setSyncState(cloud && local.revision !== cloud.revision ? "conflict" : "offline");
            setNotice(
              cloud && local.revision !== cloud.revision
                ? "Hay cambios locales y una versión más nueva en la nube. Elegí cuál conservar."
                : "Restauramos cambios locales cifrados que todavía no llegaron a la nube.",
            );
            void loadPatientDetail(patient.id);
          }
        }
      }
    } catch (reason) {
      setError(messageFor(reason, "No pudimos cargar la bandeja de atenciones."));
    } finally {
      setLoading(false);
    }
  }, [activeOrganization?.id, loadPatientDetail, user?.id, vaultScope]);

  useEffect(() => {
    setEditor(null);
    setSigned(null);
    setNotice(null);
    void load();
  }, [load]);

  useEffect(() => {
    if (
      loading ||
      editor ||
      !requestedPatientId ||
      openedRequestedPatientRef.current === requestedPatientId
    )
      return;
    const patient = patients.find(
      (item) => item.id === requestedPatientId && item.clinicalDetailsAvailable,
    );
    if (!patient) return;
    openedRequestedPatientRef.current = requestedPatientId;
    const ownDraft = drafts.find(
      (item) => item.petId === patient.id && item.authorUserId === user?.id,
    );
    if (ownDraft) openDraft(ownDraft, patients);
    else beginPatient(patient);
    const next = new URLSearchParams(searchParams);
    next.delete("patient");
    setSearchParams(next, { replace: true });
  }, [
    beginPatient,
    drafts,
    editor,
    loading,
    openDraft,
    patients,
    requestedPatientId,
    searchParams,
    setSearchParams,
    user?.id,
  ]);

  useEffect(() => {
    const retryPendingSync = (): void => {
      setOnlineRevision((current) => current + 1);
    };
    window.addEventListener("online", retryPendingSync);
    return () => window.removeEventListener("online", retryPendingSync);
  }, []);

  useEffect(() => {
    if (!editor || !vaultScope || (editor.draft && !editor.draft.canEdit)) return;
    const snapshot = draftSnapshot(editor.fields);
    void saveLocalClinicalDraft(vaultScope, {
      organizationId: activeOrganization?.id ?? "",
      petId: editor.patient.id,
      draftId: editor.draft?.id ?? null,
      revision: editor.draft?.revision ?? null,
      fields: editor.fields,
      savedAt: new Date().toISOString(),
    });
    if (!hasContent(editor.fields)) {
      setSyncState(editor.draft ? "local" : "idle");
      return;
    }
    if (snapshot === lastSavedRef.current || syncState === "conflict") return;
    setSyncState(navigator.onLine ? "pending" : "offline");
    if (persistingRef.current) return;
    const timeout = window.setTimeout(() => {
      if (!activeOrganization) return;
      persistingRef.current = true;
      const persist = editor.draft
        ? api.updateClinicalDraft(activeOrganization.id, editor.draft.id, {
            ...editor.fields,
            revision: editor.draft.revision,
          })
        : api.createClinicalDraft(activeOrganization.id, editor.patient.id, {
            ...editor.fields,
            source: window.matchMedia("(max-width: 900px)").matches ? "VET_MOBILE" : "CRM",
          });
      void persist
        .then((savedDraft) => {
          lastSavedRef.current = snapshot;
          setEditor((current) =>
            current?.patient.id === savedDraft.petId ? { ...current, draft: savedDraft } : current,
          );
          setDrafts((current) => [
            savedDraft,
            ...current.filter((item) => item.id !== savedDraft.id),
          ]);
          setSyncState("synced");
          setNotice(null);
        })
        .catch((reason: unknown) => {
          if (
            reason instanceof ApiError &&
            ["CRM_DRAFT_CHANGED", "CRM_DRAFT_ALREADY_ACTIVE"].includes(reason.code)
          ) {
            setSyncState("conflict");
            setNotice(
              "Este borrador cambió en otro dispositivo. Tus cambios siguen cifrados en este equipo.",
            );
          } else {
            setSyncState("offline");
            setNotice("Sincronización pendiente. Tus cambios siguen cifrados en este dispositivo.");
          }
        })
        .finally(() => {
          persistingRef.current = false;
        });
    }, 850);
    return () => window.clearTimeout(timeout);
  }, [
    activeOrganization?.id,
    editor?.draft?.id,
    editor?.draft?.revision,
    editor?.fields,
    editor?.patient.id,
    onlineRevision,
    syncState,
    vaultScope,
  ]);

  function updateField<Key extends keyof ClinicalDraftFields>(
    key: Key,
    value: ClinicalDraftFields[Key],
  ): void {
    setEditor((current) =>
      current ? { ...current, fields: { ...current.fields, [key]: value } } : current,
    );
    setReviewing(false);
  }

  async function reloadCloud(keepLocal: boolean): Promise<void> {
    if (!activeOrganization || !editor) return;
    setSyncState("pending");
    try {
      const nextDrafts = await api.clinicalDrafts(activeOrganization.id);
      setDrafts(nextDrafts);
      const cloud =
        nextDrafts.find((item) => item.id === editor.draft?.id) ??
        nextDrafts.find(
          (item) => item.petId === editor.patient.id && item.authorUserId === user?.id,
        );
      if (!cloud) {
        setEditor((current) => (current ? { ...current, draft: null } : current));
        setSyncState("offline");
        return;
      }
      if (keepLocal) {
        const saved = await api.updateClinicalDraft(activeOrganization.id, cloud.id, {
          ...editor.fields,
          revision: cloud.revision,
        });
        lastSavedRef.current = draftSnapshot(editor.fields);
        setEditor((current) => (current ? { ...current, draft: saved } : current));
        setDrafts((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      } else {
        const fields = {
          reason: cloud.reason,
          findings: cloud.findings,
          diagnosis: cloud.diagnosis,
          treatment: cloud.treatment,
          alertsReviewed: cloud.alertsReviewed,
        };
        lastSavedRef.current = draftSnapshot(fields);
        setEditor({ patient: editor.patient, draft: cloud, fields });
      }
      if (vaultScope) clearLocalClinicalDraft(vaultScope);
      setSyncState("synced");
      setNotice(null);
    } catch (reason) {
      setSyncState("conflict");
      setNotice(messageFor(reason, "No pudimos resolver el conflicto todavía."));
    }
  }

  async function discard(): Promise<void> {
    if (!activeOrganization || !editor) return;
    setDiscardBusy(true);
    try {
      if (editor.draft) {
        await api.discardClinicalDraft(
          activeOrganization.id,
          editor.draft.id,
          editor.draft.revision,
        );
        setDrafts((current) => current.filter((item) => item.id !== editor.draft?.id));
      }
      if (vaultScope) clearLocalClinicalDraft(vaultScope);
      setEditor(null);
      setPatientDetail(null);
      setConfirmDiscard(false);
      setSyncState("idle");
      setNotice("El borrador se descartó sin modificar la historia clínica.");
    } catch (reason) {
      setNotice(messageFor(reason, "No pudimos descartar el borrador."));
    } finally {
      setDiscardBusy(false);
    }
  }

  async function sign(): Promise<void> {
    if (!activeOrganization || !editor?.draft) return;
    setSigning(true);
    setNotice(null);
    try {
      const result = await api.signClinicalDraft(
        activeOrganization.id,
        editor.draft.id,
        editor.draft.revision,
        signIdempotencyRef.current,
      );
      if (vaultScope) clearLocalClinicalDraft(vaultScope);
      setDrafts((current) => current.filter((item) => item.id !== result.draftId));
      setSigned(result);
      setEditor(null);
      setPatientDetail(null);
      setReviewing(false);
      setSyncState("idle");
      signIdempotencyRef.current = crypto.randomUUID();
    } catch (reason) {
      if (reason instanceof ApiError && reason.code === "CRM_DRAFT_CHANGED") {
        setSyncState("conflict");
      }
      setNotice(messageFor(reason, "No pudimos firmar la atención."));
    } finally {
      setSigning(false);
    }
  }

  const filteredPatients = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("es-AR");
    return patients
      .filter((patient) => patient.clinicalDetailsAvailable)
      .filter((patient) =>
        [patient.name, patient.publicSlug, patient.activeTagCode ?? ""].some((value) =>
          value.toLocaleLowerCase("es-AR").includes(term),
        ),
      );
  }, [patients, search]);

  if (!activeOrganization) return null;

  if (signed) {
    return (
      <div className="screen clinical-mode-screen">
        <section className="clinical-signed-state" role="status">
          <span aria-hidden="true">
            <CheckCircle2 size={32} />
          </span>
          <span className="eyebrow">Versión final única</span>
          <h1>Atención firmada</h1>
          <p>
            La revisión sincronizada quedó en la historia a las {formatDate(signed.signedAt, true)}{" "}
            y el borrador ya no puede editarse.
          </p>
          <div className="form-actions">
            <button className="button button-secondary" onClick={() => setSigned(null)}>
              Volver a atenciones
            </button>
            <Link className="button button-primary" to={`/pacientes/${signed.petId}`}>
              Abrir historia clínica
            </Link>
          </div>
        </section>
      </div>
    );
  }

  if (editor) {
    const readOnly = Boolean(editor.draft && !editor.draft.canEdit);
    const readyToReview =
      Boolean(editor.draft) &&
      syncState === "synced" &&
      editor.fields.reason.trim().length >= 2 &&
      editor.fields.diagnosis.trim().length >= 2 &&
      editor.fields.alertsReviewed;
    return (
      <div className="screen clinical-mode-screen">
        <header className="clinical-mode-header">
          <button
            className="button-link"
            type="button"
            onClick={() => {
              setEditor(null);
              setPatientDetail(null);
              setReviewing(false);
            }}
          >
            <ArrowLeft size={17} /> Bandeja de atenciones
          </button>
          <ContinuityBeacon state={syncState} />
        </header>

        {notice && (
          <div
            className={`clinical-sync-notice${syncState === "conflict" ? " is-conflict" : ""}`}
            role={syncState === "conflict" ? "alert" : "status"}
          >
            {syncState === "offline" ? <WifiOff size={18} /> : <CloudOff size={18} />}
            <p>{notice}</p>
            {syncState === "conflict" && (
              <div>
                <button
                  className="button button-secondary button-compact"
                  onClick={() => void reloadCloud(false)}
                >
                  Cargar versión nube
                </button>
                <button
                  className="button button-primary button-compact"
                  onClick={() => void reloadCloud(true)}
                >
                  Guardar mi versión
                </button>
              </div>
            )}
          </div>
        )}

        <section className="clinical-patient-anchor" aria-labelledby="attention-patient-title">
          <span className="patient-avatar patient-avatar-large" aria-hidden="true">
            {editor.patient.name.charAt(0).toUpperCase()}
          </span>
          <div>
            <span className="eyebrow">Atención en curso</span>
            <h1 id="attention-patient-title">{editor.patient.name}</h1>
            <p>
              {patientKind(editor.patient.species)} · {editor.patient.breed ?? "Sin raza indicada"}
            </p>
          </div>
          {editor.draft && <span className="draft-revision">Revisión {editor.draft.revision}</span>}
        </section>

        <div className="clinical-mode-layout">
          <aside className="clinical-alert-rail" aria-labelledby="clinical-alerts-title">
            <div>
              <ShieldCheck size={18} aria-hidden="true" />
              <span>
                <small>Antes de escribir</small>
                <strong id="clinical-alerts-title">Alertas clínicas</strong>
              </span>
            </div>
            {loadingDetail ? (
              <p role="status">
                <LoaderCircle className="spin" size={16} /> Cargando alertas…
              </p>
            ) : patientDetail?.medicalSummary ? (
              <dl>
                <div>
                  <dt>Alergias</dt>
                  <dd>{patientDetail.medicalSummary.allergies ?? "Sin alertas registradas"}</dd>
                </div>
                <div>
                  <dt>Condiciones</dt>
                  <dd>
                    {patientDetail.medicalSummary.conditions ?? "Sin condiciones registradas"}
                  </dd>
                </div>
                <div>
                  <dt>Medicación</dt>
                  <dd>
                    {patientDetail.medicalSummary.medications ??
                      `${patientDetail.medications.filter((item) => item.status === "ACTIVE").length} indicaciones activas`}
                  </dd>
                </div>
              </dl>
            ) : (
              <p>No hay resumen clínico cargado por el tutor.</p>
            )}
            {!readOnly && (
              <label className="clinical-alert-confirm">
                <input
                  type="checkbox"
                  checked={editor.fields.alertsReviewed}
                  onChange={(event) => updateField("alertsReviewed", event.target.checked)}
                />
                <span>
                  <strong>Revisé las alertas</strong>
                  <small>Esta confirmación queda ligada a la versión firmada.</small>
                </span>
              </label>
            )}
          </aside>

          <section className="clinical-editor-panel">
            <div className="clinical-editor-heading">
              <div>
                <span className="eyebrow">
                  {editor.draft?.source === "VET_MOBILE"
                    ? "Iniciado en móvil"
                    : "Historia en preparación"}
                </span>
                <h2>{readOnly ? `Borrador de ${editor.draft?.authorName}` : "Registro clínico"}</h2>
              </div>
              {!readOnly && editor.draft && (
                <button
                  className="button-link button-link-danger"
                  onClick={() => setConfirmDiscard(true)}
                  type="button"
                >
                  <Trash2 size={16} /> Descartar
                </button>
              )}
            </div>

            {reviewing ? (
              <ClinicalDraftReview
                fields={editor.fields}
                patientName={editor.patient.name}
                revision={editor.draft?.revision ?? 0}
                signing={signing}
                onBack={() => setReviewing(false)}
                onSign={() => void sign()}
              />
            ) : (
              <div className="clinical-editor-fields">
                <label>
                  <span>Motivo de consulta</span>
                  <input
                    value={editor.fields.reason}
                    onChange={(event) => updateField("reason", event.target.value)}
                    maxLength={500}
                    placeholder="Qué trae hoy al paciente"
                    autoFocus={!editor.draft}
                    disabled={readOnly}
                  />
                </label>
                <label>
                  <span>Examen y observaciones</span>
                  <textarea
                    value={editor.fields.findings}
                    onChange={(event) => updateField("findings", event.target.value)}
                    maxLength={4000}
                    rows={5}
                    placeholder="Hallazgos relevantes, parámetros y evolución"
                    disabled={readOnly}
                  />
                </label>
                <label>
                  <span>Diagnóstico</span>
                  <textarea
                    value={editor.fields.diagnosis}
                    onChange={(event) => updateField("diagnosis", event.target.value)}
                    maxLength={2000}
                    rows={3}
                    placeholder="Diagnóstico confirmado, presuntivo o diferencial"
                    disabled={readOnly}
                  />
                </label>
                <label>
                  <span>Tratamiento e indicaciones</span>
                  <textarea
                    value={editor.fields.treatment}
                    onChange={(event) => updateField("treatment", event.target.value)}
                    maxLength={4000}
                    rows={5}
                    placeholder="Plan, indicaciones al tutor y próximo control"
                    disabled={readOnly}
                  />
                </label>
                {!readOnly && (
                  <div className="clinical-editor-actions">
                    <p>
                      <LockKeyhole size={15} aria-hidden="true" />
                      La historia todavía no cambió. Solo se firma la revisión sincronizada.
                    </p>
                    <button
                      className="button button-primary"
                      type="button"
                      disabled={!readyToReview}
                      onClick={() => setReviewing(true)}
                    >
                      <Check size={17} /> Revisar y firmar
                    </button>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>

        {confirmDiscard && (
          <div className="clinical-confirm-bar" role="alert">
            <div>
              <strong>¿Descartar este borrador?</strong>
              <p>La historia firmada no cambia y esta versión deja de estar disponible.</p>
            </div>
            <button
              className="button button-secondary button-compact"
              onClick={() => setConfirmDiscard(false)}
              disabled={discardBusy}
            >
              Cancelar
            </button>
            <button
              className="button button-danger button-compact"
              onClick={() => void discard()}
              disabled={discardBusy}
            >
              {discardBusy ? <LoaderCircle className="spin" size={16} /> : null}
              {discardBusy ? "Descartando…" : "Descartar borrador"}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="screen clinical-mode-screen">
      <header className="page-header clinical-inbox-header">
        <div>
          <span className="eyebrow">Continuidad móvil ↔ CRM</span>
          <h1>Atenciones</h1>
          <p>Borradores compartidos que terminan en una única versión firmada.</p>
        </div>
        <button className="button button-primary" onClick={() => setStarting(true)}>
          <Stethoscope size={18} /> Nueva atención
        </button>
      </header>

      {notice && (
        <div className="clinical-sync-notice" role="status">
          <Cloud size={18} />
          <p>{notice}</p>
          <button className="icon-button" onClick={() => setNotice(null)} aria-label="Cerrar aviso">
            <X size={17} />
          </button>
        </div>
      )}
      {error && (
        <div className="error-panel" role="alert">
          <CloudOff size={20} />
          <div>
            <strong>No pudimos abrir la continuidad clínica</strong>
            <p>{error}</p>
          </div>
          <button className="button button-secondary button-compact" onClick={() => void load()}>
            <RefreshCw size={16} /> Reintentar
          </button>
        </div>
      )}

      <section className="continuity-strip" aria-label="Cómo funciona la continuidad">
        <div>
          <LockKeyhole size={18} />
          <span>
            <strong>1. Borrador cifrado</strong>
            <small>El dispositivo protege cada cambio.</small>
          </span>
        </div>
        <span aria-hidden="true" />
        <div>
          <Cloud size={18} />
          <span>
            <strong>2. Nube clínica</strong>
            <small>La revisión aparece en todo el CRM.</small>
          </span>
        </div>
        <span aria-hidden="true" />
        <div>
          <CheckCircle2 size={18} />
          <span>
            <strong>3. Firma única</strong>
            <small>Solo esa versión entra a la historia.</small>
          </span>
        </div>
      </section>

      {starting && (
        <section className="panel clinical-patient-picker" aria-labelledby="patient-picker-title">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Paso 1</span>
              <h2 id="patient-picker-title">Elegí el paciente</h2>
            </div>
            <button className="icon-button" onClick={() => setStarting(false)} aria-label="Cerrar">
              <X size={19} />
            </button>
          </div>
          <label className="search-field">
            <Search size={18} aria-hidden="true" />
            <span className="sr-only">Buscar paciente</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Nombre, tag o identificador"
              autoFocus
            />
          </label>
          <div className="clinical-patient-options">
            {filteredPatients.slice(0, 8).map((patient) => (
              <button key={patient.id} onClick={() => beginPatient(patient)}>
                <span className="patient-avatar" aria-hidden="true">
                  {patient.name.charAt(0).toUpperCase()}
                </span>
                <span>
                  <strong>{patient.name}</strong>
                  <small>
                    {patientKind(patient.species)} · {patient.breed ?? "Sin raza indicada"}
                  </small>
                </span>
                <PawPrint size={17} />
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="panel clinical-draft-inbox" aria-labelledby="draft-inbox-title">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">Trabajo en curso</span>
            <h2 id="draft-inbox-title">Borradores activos</h2>
          </div>
          <span className="count">{drafts.length}</span>
        </div>
        {loading ? (
          <div className="clinical-draft-skeleton" role="status" aria-label="Cargando borradores">
            <span />
            <span />
          </div>
        ) : drafts.length ? (
          <div className="clinical-draft-list">
            {drafts.map((draft) => (
              <button key={draft.id} onClick={() => openDraft(draft, patients)}>
                <span className="draft-source" aria-hidden="true">
                  {draft.source === "VET_MOBILE" ? (
                    <Stethoscope size={19} />
                  ) : (
                    <FilePenLine size={19} />
                  )}
                </span>
                <span className="draft-main">
                  <span>
                    <strong>{draft.petName}</strong>
                    <small>{draft.reason || "Sin motivo todavía"}</small>
                  </span>
                  <span className="draft-meta">
                    {draft.authorUserId === user?.id ? "Tu borrador" : draft.authorName} · revisión{" "}
                    {draft.revision} · {formatDate(draft.updatedAt, true)}
                  </span>
                </span>
                <span className={draft.canEdit ? "draft-owner" : "draft-shared"}>
                  {draft.canEdit ? (
                    <>
                      <Cloud size={14} /> Continuar
                    </>
                  ) : (
                    <>
                      <UsersRound size={14} /> Solo lectura
                    </>
                  )}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="clinical-draft-empty">
            <span aria-hidden="true">
              <FilePenLine size={26} />
            </span>
            <h3>No hay atenciones en borrador</h3>
            <p>Empezá desde acá o desde un teléfono; aparecerá en ambos lugares.</p>
            <button
              className="button button-secondary button-compact"
              onClick={() => setStarting(true)}
            >
              Elegir paciente
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

function ContinuityBeacon({ state }: { state: SyncState }): ReactNode {
  const content: Record<SyncState, { label: string; icon: ReactNode }> = {
    idle: { label: "Esperando contenido", icon: <LockKeyhole size={16} /> },
    local: { label: "Solo en este dispositivo", icon: <LockKeyhole size={16} /> },
    pending: { label: "Sincronizando…", icon: <LoaderCircle className="spin" size={16} /> },
    synced: { label: "Sincronizado", icon: <Cloud size={16} /> },
    offline: { label: "Cifrado · pendiente", icon: <WifiOff size={16} /> },
    conflict: { label: "Revisión en conflicto", icon: <CloudOff size={16} /> },
  };
  return (
    <span className={`continuity-beacon continuity-beacon-${state}`} role="status">
      {content[state].icon}
      {content[state].label}
    </span>
  );
}

function ClinicalDraftReview({
  fields,
  patientName,
  revision,
  signing,
  onBack,
  onSign,
}: {
  fields: ClinicalDraftFields;
  patientName: string;
  revision: number;
  signing: boolean;
  onBack: () => void;
  onSign: () => void;
}): ReactNode {
  return (
    <section className="clinical-review" aria-labelledby="clinical-review-title">
      <div className="clinical-review-title">
        <span aria-hidden="true">
          <ShieldCheck size={21} />
        </span>
        <div>
          <span className="eyebrow">Revisión {revision}</span>
          <h3 id="clinical-review-title">Versión final de {patientName}</h3>
          <p>Después de firmar, cualquier corrección será una adenda.</p>
        </div>
      </div>
      <dl>
        <div>
          <dt>Motivo</dt>
          <dd>{fields.reason}</dd>
        </div>
        <div>
          <dt>Hallazgos</dt>
          <dd>{fields.findings || "Sin hallazgos adicionales"}</dd>
        </div>
        <div>
          <dt>Diagnóstico</dt>
          <dd>{fields.diagnosis}</dd>
        </div>
        <div>
          <dt>Tratamiento</dt>
          <dd>{fields.treatment || "Sin tratamiento indicado"}</dd>
        </div>
        <div>
          <dt>Alertas</dt>
          <dd>Revisadas explícitamente</dd>
        </div>
      </dl>
      <div className="clinical-review-warning">
        <LockKeyhole size={17} />
        <p>Se firmará exactamente esta revisión sincronizada. La acción es auditable.</p>
      </div>
      <div className="form-actions">
        <button className="button button-secondary" onClick={onBack} disabled={signing}>
          Volver a editar
        </button>
        <button className="button button-primary" onClick={onSign} disabled={signing}>
          {signing ? <LoaderCircle className="spin" size={17} /> : <CheckCircle2 size={17} />}
          Firmar atención
        </button>
      </div>
    </section>
  );
}
